import React, { useState } from "react";
import { ChevronDown, Calendar as CalendarIcon } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";

/**
 * Collapsible "Dates" section that appears directly below the Schedule
 * section on both single- and multi-location job dialogs.
 *
 * Props:
 *   createdAt          — ISO string or "" (prefer ISO with time)
 *   completedAt        — ISO string or ""
 *   scheduledDate      — yyyy-mm-dd string (mirrors what's already in Schedule)
 *   isCompleted        — boolean; if false, completed date input stays blank by default
 *   onChange({created_at, completed_at})
 *   scheduledDateHint  — readonly display of the scheduled date (scheduled date is
 *                         still primarily edited in the Schedule section to avoid
 *                         double sources of truth)
 *   defaultOpen        — optional override (default false)
 */
const JobDatesSection = ({
  createdAt,
  completedAt,
  scheduledDate,
  isCompleted,
  onChange,
  defaultOpen = false,
  isDark = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // Convert ISO strings to the `datetime-local` input format (yyyy-MM-ddTHH:mm)
  const toDatetimeLocal = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };

  // datetime-local → ISO for backend
  const fromDatetimeLocal = (local) => (local ? new Date(local).toISOString() : "");

  return (
    <div className={`rounded-lg border ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50/40"}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
        data-testid="job-dates-toggle"
      >
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Dates</span>
          <span className="text-[11px] text-muted-foreground">
            · Created / Scheduled / Completed
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-border" data-testid="job-dates-section">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Created Date</Label>
              <Input
                type="datetime-local"
                value={toDatetimeLocal(createdAt)}
                onChange={(e) => onChange({ created_at: fromDatetimeLocal(e.target.value) })}
                className="h-9"
                data-testid="job-dates-created-at"
              />
              <p className="text-[10px] text-muted-foreground">Defaults to now if left blank.</p>
            </div>

            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Scheduled Date</Label>
              <Input
                type="text"
                value={scheduledDate || ""}
                readOnly
                className="h-9 bg-muted/40 cursor-not-allowed"
                data-testid="job-dates-scheduled-date"
              />
              <p className="text-[10px] text-muted-foreground">Edit this in the Schedule section above.</p>
            </div>

            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Completed Date</Label>
              <Input
                type="datetime-local"
                value={toDatetimeLocal(completedAt)}
                onChange={(e) => onChange({ completed_at: fromDatetimeLocal(e.target.value) })}
                className="h-9"
                placeholder="—"
                disabled={!isCompleted && !completedAt}
                data-testid="job-dates-completed-at"
              />
              <p className="text-[10px] text-muted-foreground">
                {isCompleted || completedAt
                  ? "Set when this job was finished."
                  : "Blank until the job is completed."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDatesSection;
